// ========= START: src/components/PronunciationChallenge.js (COMPLETE REPLACEMENT) =========
import React, { useState, useEffect, useCallback, useRef } from 'react';
import vocabulary from '../data/pronunciation_vocab.js';
import audioManager from '../utils/audioManager';

import PronunciationMainMenu from './PronunciationMainMenu';
import GameSetupScreen from './GameSetupScreen';
import GamePlayScreen from './GamePlayScreen';
import FinalStatsScreen from './FinalStatsScreen';
import CharacterCustomizationScreen from './CharacterCustomizationScreen';
import SettingsScreen from './SettingsScreen';
import Background from './Background';

import './PronunciationChallenge.css';

function PronunciationChallenge({
  phase,
  onNavigate,
  onExit,
  onNavigateToApp,
  speechRecognition,
  playerData,
  onUpdatePlayerData,
  onAddXp
}) {

  const [userInteracted, setUserInteracted] = useState(false);
  const [gameSettings, setGameSettings] = useState({
    grade: 3,
    categories: [],
    wordCount: 10,
  });
  const [gameplayState, setGameplayState] = useState({
    wordList: [],
    currentWordIndex: 0,
    score: 0,
    statusMessage: 'Tap GO! to start',
    wordStartTime: 0,
    isGameActive: false,
    resultImage: null,
    currentSpeed: 5,
    consecutivePerfects: 0,
  });
  const [settings, setSettings] = useState({
    bgmVolume: 0.1,
    sfxVolume: 0.8,
  });
  const [isMuted, setIsMuted] = useState(audioManager.isMuted); // NEW: Mute state

  const { finalTranscript, isListening, startListening, stopListening, resetTranscript } = speechRecognition;

  const listenTimer = useRef(null);

  // --- Core Game Logic ---
  const handleToggleMute = () => {
    const newMuteState = audioManager.toggleMute();
    setIsMuted(newMuteState);
  };

  const handleBack = () => {
    clearTimeout(listenTimer.current);
    stopListening();
    audioManager.playSFX('sfxClick');
    if (phase === 'MENU') onExit();
    else onNavigate('MENU');
  };

  const handleSavePlayerData = (newPlayerData) => {
    onUpdatePlayerData(newPlayerData);
    audioManager.playSFX('sfxClick');
  };

  const isRoundOver = (wordList) => {
    if (!wordList || wordList.length === 0) return false;
    return wordList.every(word => word.status === 'cleared' || word.status === 'skipped');
  };

  const startWordAttempt = useCallback(() => {
    clearTimeout(listenTimer.current);
    resetTranscript();
    startListening();
    setGameplayState(prev => ({ ...prev, statusMessage: 'Listening...' }));

    listenTimer.current = setTimeout(() => {
      if (isListening) {
        stopListening();
      }
    }, 3500);
  }, [startListening, stopListening, resetTranscript, isListening]);

  const handleNextWord = useCallback(() => {
    setGameplayState(prev => {
      if (isRoundOver(prev.wordList)) return prev;

      const findNextIndex = (start) => prev.wordList.findIndex((w, i) => i > start && w.status !== 'cleared' && w.status !== 'skipped');
      let nextWordIndex = findNextIndex(prev.currentWordIndex);
      if (nextWordIndex === -1) nextWordIndex = findNextIndex(-1);

      if (nextWordIndex !== -1) {
        return { ...prev, currentWordIndex: nextWordIndex, wordStartTime: Date.now(), resultImage: null };
      }
      return prev;
    });
  }, []);

  const handleMicPress = () => {
    audioManager.playSFX('sfxClick');
    if (!gameplayState.isGameActive) {
      setGameplayState(prev => ({ ...prev, isGameActive: true, wordStartTime: Date.now() }));
    }
  };

  const handleGiveUp = () => {
    if (!gameplayState.isGameActive) return;
    clearTimeout(listenTimer.current);
    stopListening();
    audioManager.playSFX('sfxClick');

    const updatedWordList = [...gameplayState.wordList];
    updatedWordList[gameplayState.currentWordIndex].status = 'skipped';
    updatedWordList[gameplayState.currentWordIndex].score = 0;

    setGameplayState(prev => ({
      ...prev,
      wordList: updatedWordList,
      resultImage: null,
      statusMessage: 'Skipped!'
    }));

    // Auto-sync will handle moving to the next index
  };

  // --- Effect Hooks ---
  useEffect(() => {
    return () => {
      audioManager.stopBGM();
      clearTimeout(listenTimer.current);
    };
  }, []);

  useEffect(() => {
    if (gameplayState.isGameActive && phase === 'PLAY') {
      startWordAttempt();
    }
  }, [gameplayState.isGameActive, gameplayState.currentWordIndex, startWordAttempt, phase]);

  useEffect(() => {
    let requiredBgm;
    switch (phase) {
      case 'MENU': case 'STATS': case 'CHARACTER': case 'SETTINGS': case 'SETUP': requiredBgm = 'gameBgmMenu'; break;
      case 'PLAY': requiredBgm = 'bgmPlay'; break;
      default: requiredBgm = null; break;
    }
    if (audioManager.currentBgmKey !== requiredBgm) {
      if (requiredBgm) audioManager.playBGM(requiredBgm);
      else audioManager.stopBGM();
    }
  }, [phase]);

  useEffect(() => {
    audioManager.setBgmVolume(settings.bgmVolume);
    audioManager.setSfxVolume(settings.sfxVolume);
  }, [settings]);

  useEffect(() => {
    if (phase === 'PLAY' && gameSettings.categories.length > 0) {
      const gradeWords = vocabulary.filter(word => word.grade <= gameSettings.grade);
      const categoryWords = gradeWords.filter(word => gameSettings.categories.includes(word.category));
      let finalWordPool = [];
      if (categoryWords.length > 0) {
        if (gameSettings.wordCount <= categoryWords.length) {
          finalWordPool = [...categoryWords].sort(() => 0.5 - Math.random()).slice(0, gameSettings.wordCount);
        } else {
          finalWordPool = [...categoryWords];
          const wordsNeeded = gameSettings.wordCount - categoryWords.length;
          for (let i = 0; i < wordsNeeded; i++) {
            finalWordPool.push(categoryWords[Math.floor(Math.random() * categoryWords.length)]);
          }
          finalWordPool.sort(() => 0.5 - Math.random());
        }
      }
      const detailedWordList = finalWordPool.map((w, index) => ({
        ...w,
        instanceId: `${w.id}_${index}`,
        status: 'pending',
        time: 0,
        score: 0,
        attempts: 0,
        isPerfect: false
      }));
      setGameplayState({
        wordList: detailedWordList,
        currentWordIndex: 0,
        score: 0,
        statusMessage: 'Tap GO! to start',
        wordStartTime: Date.now(),
        isGameActive: false,
        resultImage: null,
        currentSpeed: 5,
        consecutivePerfects: 0
      });
    }
  }, [phase, gameSettings]);

  // Tracking for speech recognition to prevent double-processing
  const lastProcessedTranscriptRef = useRef('');

  useEffect(() => {
    // RESET lock when transcript is empty (ready for next sound)
    if (!finalTranscript) {
      lastProcessedTranscriptRef.current = '';
      return;
    }

    if (!gameplayState.isGameActive) return;

    // PREVENT DOUBLE-TRIGGER: If we already processed this exact transcript, ignore re-runs
    if (finalTranscript === lastProcessedTranscriptRef.current) return;

    // Guard: Identify the target word using the LATEST state
    const activeIndex = gameplayState.wordList.findIndex(w => w.status === 'pending');
    if (activeIndex === -1) return;

    // Mark as processed IMMEDIATELY
    lastProcessedTranscriptRef.current = finalTranscript;
    resetTranscript();

    clearTimeout(listenTimer.current);
    stopListening();

    let isCorrect = false;
    let newResultImage = 'incorrect';
    let currentScore = gameplayState.score;
    const newWordList = [...gameplayState.wordList];
    const currentWordState = { ...newWordList[activeIndex] };
    const currentWordInfo = gameplayState.wordList[activeIndex];
    currentWordState.attempts += 1;

    // NOISE/PUNCTUATION ROBUST MATCHING
    const normalizedTranscript = finalTranscript.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
    const noSpaceTranscript = normalizedTranscript.replace(/\s/g, "");

    const targetWord = currentWordInfo.word.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim();
    const noSpaceTarget = targetWord.replace(/\s/g, "");

    const alternatives = (currentWordInfo.alt || []).map(a => a.toLowerCase().replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, "").trim());
    const noSpaceAlts = alternatives.map(a => a.replace(/\s/g, ""));

    // Check for exact match (with or without spaces)
    const isExact = normalizedTranscript === targetWord ||
      noSpaceTranscript === noSpaceTarget ||
      alternatives.includes(normalizedTranscript) ||
      noSpaceAlts.includes(noSpaceTranscript);

    // Check for contained match
    const transcriptWords = normalizedTranscript.split(' ');
    const isContained = transcriptWords.includes(targetWord) ||
      alternatives.some(alt => transcriptWords.includes(alt));

    console.log(`[Recognition Debug] Word: "${targetWord}", Heard: "${normalizedTranscript}", Match: ${isExact || isContained} (Index: ${activeIndex})`);
    console.log(`[Status Tracker] Before update:`, gameplayState.wordList.map(w => `${w.word}:${w.status}`).join(', '));

    if (isExact || isContained) {
      isCorrect = true;
      const timeTaken = (Date.now() - gameplayState.wordStartTime) / 1000;

      let baseScore = 0;
      if (timeTaken <= 2.0) baseScore = 50; else if (timeTaken <= 3.0) baseScore = 40;
      else if (timeTaken <= 5.0) baseScore = 30; else if (timeTaken <= 10.0) baseScore = 15;
      else baseScore = 5;

      let perfectBonus = 0;
      const isPerfectAttempt = timeTaken <= 2.0 && currentWordState.attempts === 1;
      if (isPerfectAttempt) {
        perfectBonus = 25;
        currentWordState.isPerfect = true;
        newResultImage = 'perfect';
        audioManager.playSFX('sfxLevelUp');
      } else {
        newResultImage = 'correct';
        audioManager.playSFX('sfxCorrect');
      }

      const masteryData = (playerData.wordMastery && playerData.wordMastery[currentWordInfo.id]) || { level: 'New' };
      let masteryMultiplier = 1.0;
      if (masteryData.level === 'Learning') masteryMultiplier = 0.75;
      else if (masteryData.level === 'Practiced') masteryMultiplier = 0.50;
      else if (masteryData.level === 'Mastered') masteryMultiplier = 0.10;

      const finalPoints = Math.round((baseScore + perfectBonus) * masteryMultiplier);
      currentScore += finalPoints;

      currentWordState.time = timeTaken;
      currentWordState.status = 'cleared';
      currentWordState.score = finalPoints;
      newWordList[activeIndex] = currentWordState;

      const newPlayerData = JSON.parse(JSON.stringify(playerData));
      if (!newPlayerData.wordMastery) newPlayerData.wordMastery = {};

      let newMasteryLevel = masteryData.level;
      if (isPerfectAttempt) {
        if (newMasteryLevel === 'New') newMasteryLevel = 'Learning';
        else if (newMasteryLevel === 'Learning') newMasteryLevel = 'Practiced';
        else if (newMasteryLevel === 'Practiced') newMasteryLevel = 'Mastered';
      } else if (newMasteryLevel === 'New') newMasteryLevel = 'Learning';

      newPlayerData.wordMastery[currentWordInfo.id] = { level: newMasteryLevel, lastPracticed: new Date().toISOString() };
      onUpdatePlayerData(newPlayerData);
      onAddXp(finalPoints);
    } else {
      audioManager.playSFX('sfxClick');
    }

    setGameplayState(prev => {
      let nextSpeed = prev.currentSpeed;
      let nextConsecutivePerfects = prev.consecutivePerfects;

      if (isCorrect) {
        if (newResultImage === 'perfect') {
          nextConsecutivePerfects += 1;
          nextSpeed = Math.min(20, nextSpeed + 3.0); // Increase by 3 notches, cap at 20
        } else if (newResultImage === 'correct') {
          nextConsecutivePerfects = 0;
          nextSpeed = Math.max(5, nextSpeed - 1.0); // Decrease by 1 notch, floor at 5
        }
      } else if (newResultImage === 'incorrect') {
        nextConsecutivePerfects = 0;
      }

      console.log(`[Speed Update] Result: ${newResultImage}, Old Speed: ${prev.currentSpeed}, New Speed: ${nextSpeed}`);

      return {
        ...prev,
        wordList: newWordList,
        resultImage: newResultImage,
        score: currentScore,
        statusMessage: isCorrect ? (newResultImage === 'perfect' ? 'PERFECT!' : 'Correct!') : 'Try Again!',
        currentSpeed: nextSpeed,
        consecutivePerfects: nextConsecutivePerfects
      };
    });

    // Removed handleNextWord() from here. Auto-sync Effect below handles index progression.
    setTimeout(() => {
      if (isRoundOver(newWordList)) {
        onNavigate('STATS');
      } else if (!isCorrect) {
        setGameplayState(prev => ({ ...prev, resultImage: null }));
        startWordAttempt();
      }
    }, 2000);

  }, [finalTranscript, gameplayState.isGameActive, gameplayState.wordList]);

  // AUTO-SYNC EFFECT: Advance currentWordIndex when current word is cleared or skipped
  useEffect(() => {
    if (!gameplayState.isGameActive) return;

    const currentWord = gameplayState.wordList[gameplayState.currentWordIndex];
    if (currentWord && currentWord.status !== 'pending') {
      // Small delay before moving to help visual continuity
      const syncTimer = setTimeout(() => {
        setGameplayState(prev => {
          // Find the next word that isn't cleared/skipped
          const nextIndex = prev.wordList.findIndex((w, i) => i > prev.currentWordIndex && w.status === 'pending');

          if (nextIndex !== -1) {
            console.log(`[Auto-Sync] Word ${prev.currentWordIndex} (${currentWord.word}) finished. Moving to ${nextIndex}.`);
            return {
              ...prev,
              currentWordIndex: nextIndex,
              wordStartTime: Date.now(),
              resultImage: null
            };
          }
          return prev;
        });
      }, 800);
      return () => clearTimeout(syncTimer);
    }
  }, [gameplayState.wordList, gameplayState.currentWordIndex, gameplayState.isGameActive]);

  // RESET TRANSCRIPT TRACKING ON GAME START
  useEffect(() => {
    if (gameplayState.isGameActive) {
      lastProcessedTranscriptRef.current = '';
    }
  }, [gameplayState.isGameActive]);

  // ========= START: PronunciationChallenge.js - renderContent function (REPLACEMENT) =========
  const renderContent = () => {
    if (!playerData) return <div>Loading Player Info...</div>;

    // MODIFICATION: Removed audioManager.unlockAudio() calls. They are no longer needed here.
    switch (phase) {
      case 'MENU':
        return <PronunciationMainMenu
          user={playerData}
          onStart={() => { setUserInteracted(true); audioManager.playSFX('sfxClick'); onNavigate('SETUP'); }}
          onCharacter={() => { setUserInteracted(true); audioManager.playSFX('sfxClick'); onNavigate('CHARACTER'); }}
          onSettings={() => { setUserInteracted(true); audioManager.playSFX('sfxClick'); onNavigate('SETTINGS'); }}
        />;

      case 'CHARACTER':
        return <CharacterCustomizationScreen user={playerData} onBack={handleBack} onSavePlayerData={handleSavePlayerData} />;
      case 'SETTINGS':
        return <SettingsScreen settings={settings} onSettingsChange={setSettings} onBack={handleBack} />;
      case 'SETUP':
        return <GameSetupScreen
          gameSettings={gameSettings}
          onGameSettingsChange={setGameSettings}
          onStartGame={() => { setUserInteracted(true); audioManager.playSFX('sfxClick'); onNavigate('PLAY'); }}
        />;
      case 'PLAY':
        return <GamePlayScreen
          score={gameplayState.score}
          wordList={gameplayState.wordList}
          currentWordIndex={gameplayState.currentWordIndex}
          isGameActive={gameplayState.isGameActive}
          resultImage={gameplayState.resultImage}
          statusMessage={gameplayState.statusMessage}
          speed={gameplayState.currentSpeed}
          onMicPress={handleMicPress}
          onGiveUp={handleGiveUp}
        />;
      case 'STATS':
        return <FinalStatsScreen finalScore={gameplayState.score} wordList={gameplayState.wordList} onMainMenu={() => onNavigate('MENU')} playerData={playerData} />;
      default:
        return <div>Loading Game...</div>;
    }
  };
  // ========= END: PronunciationChallenge.js - renderContent function (REPLACEMENT) =========

  const getBackgroundConfig = () => {
    switch (phase) {
      case 'PLAY': return { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/game-play-bg.mp4` };
      case 'STATS': return { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/stats-bg.mp4` };
      case 'MENU':
      case 'CHARACTER':
      case 'SETTINGS':
      case 'SETUP': return { type: 'video', src: `${process.env.PUBLIC_URL}/assets/backgrounds/main_background.mp4` };
      default: return { type: 'none' };
    }
  };

  return (
    <div className="pronunciation-challenge-container" style={{ height: '100%', width: '100%', display: 'flex', flexDirection: 'column' }}>
      <Background background={getBackgroundConfig()} />
      <header className="App-header">
        <div className="header-buttons-left">
          {/* MODIFICATION: Simplified logic for the Character screen header */}
          {phase === 'CHARACTER' ? (
            <button onClick={() => onNavigate('MENU')} className="back-button">&lt; Menu</button>
          ) : (
            <button onClick={handleBack} className="back-button">
              {/* The back button on the Menu screen now leads back to the Welcome page via onExit */}
              {phase === 'SETTINGS' || phase === 'SETUP' ? '< Menu' :
                (<svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z" fill="currentColor" /></svg>)
              }
            </button>
          )}
        </div>
        <div className="header-title-container"><h1>Pronunciation Challenge</h1></div>
        {/* NEW: Right-side header container with Mute Button */}
        <div className="header-buttons-right">
          <button onClick={handleToggleMute} className="mute-button" title={isMuted ? "Unmute" : "Mute"}>
            {isMuted ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M16.5 12C16.5 10.23 15.48 8.71 14 7.97V10.18L16.45 12.63C16.48 12.43 16.5 12.22 16.5 12ZM19 12C19 12.82 18.87 13.61 18.64 14.34L20.05 15.75C20.68 14.63 21 13.36 21 12C21 7.72 18.28 4.21 14 3.23V5.29C17.18 6.25 19 8.83 19 12ZM4.27 3L3 4.27L7.73 9H3V15H7L12 20V13.27L16.25 17.52C15.58 17.83 14.83 18.06 14 18.18V20.24C15.38 19.93 16.63 19.26 17.68 18.36L19.73 20.41L21 19.14L4.27 3ZM12 4L9.91 6.09L12 8.18V4Z" /></svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg"><path d="M3 9V15H7L12 20V4L7 9H3ZM16.5 12c0-1.77-1.02-3.29-2.5-4.03v8.05c1.48-.73 2.5-2.25 2.5-4.02zM14 4.45v.2c0 .38.25.71.6.85C17.18 6.53 19 9.06 19 12s-1.82 5.47-4.4 6.5c-.35.14-.6.47-.6.85v.2c0 .63.63 1.07 1.21.85C18.6 19.11 21 15.84 21 12s-2.4-7.11-5.79-8.4c-.58-.23-1.21.22-1.21.85z" /></svg>
            )}
          </button>
        </div>
      </header>
      <main className="main-content-area">
        {renderContent()}
      </main>
    </div>
  );
}

export default PronunciationChallenge;
// ========= END: src/components/PronunciationChallenge.js (COMPLETE REPLACEMENT) =========